import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck, Save, Loader2, Info, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { LaadBalk } from "@/components/LaadIndicator";
import { VerzendingHealthBanner } from "@/components/VerzendingHealthBanner";

// Vlag-emoji per land (visueel accent — geen data)
const VLAG = { "Nederland": "🇳🇱", "België": "🇧🇪", "Duitsland": "🇩🇪", "Frankrijk": "🇫🇷" };

// Land-naam → cc-code, voor het matchen van de MyParcel-config aan onze webshop-lijst
const NAAM_NAAR_CC = { "Nederland": "NL", "België": "BE", "Duitsland": "DE", "Frankrijk": "FR" };

export default function InstellingenVerzending() {
  // MyParcel: verzendpartij per land
  const [config, setConfig] = useState(null);
  const [landen, setLanden] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [busy, setBusy] = useState(false);

  // Webshop: verzendregels (minimum, kosten, gratis-vanaf) per land
  const [regels, setRegels] = useState([]);
  const [regelsLoading, setRegelsLoading] = useState(true);
  const [savingLand, setSavingLand] = useState(null);
  const [edit, setEdit] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/instellingen/verzending");
      setConfig(data.config);
      setLanden(data.landen);
      setCarriers(data.carriers);
    } catch (e) {
      toast.error("MyParcel-instellingen laden mislukt");
    }
  };

  const loadRegels = async () => {
    setRegelsLoading(true);
    try {
      const { data } = await api.get("/shop/verzendregels-config");
      setRegels(data || []);
      const initEdit = {};
      (data || []).forEach((r) => {
        initEdit[r.land] = {
          minimum_bestelbedrag: r.minimum_bestelbedrag ?? "",
          verzendkosten: r.verzendkosten ?? "",
          gratis_verzending_vanaf: r.gratis_verzending_vanaf ?? "",
          valuta: r.valuta || "EUR",
        };
      });
      setEdit(initEdit);
    } catch { /* stil */ }
    setRegelsLoading(false);
  };

  useEffect(() => { load(); loadRegels(); }, []);

  // MyParcel opslaan
  const setCarrier = (cc, id) => setConfig((c) => ({ ...c, [cc]: parseInt(id, 10) }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/instellingen/verzending", { config });
      setConfig(data.config);
      toast.success("Verzendpartijen opgeslagen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally { setBusy(false); }
  };

  // Webshop-regels opslaan
  const zet = (land, veld, waarde) =>
    setEdit((e) => ({ ...e, [land]: { ...e[land], [veld]: waarde } }));

  const bewaarRegel = async (land) => {
    setSavingLand(land);
    try {
      const w = edit[land] || {};
      const num = (v) => (v === "" || v == null ? null : Number(String(v).replace(",", ".")));
      const body = {
        minimum_bestelbedrag: num(w.minimum_bestelbedrag),
        verzendkosten: num(w.verzendkosten),
        gratis_verzending_vanaf: num(w.gratis_verzending_vanaf),
        valuta: (w.valuta || "EUR").trim() || "EUR",
      };
      await api.put(`/shop/verzendregels-config/${encodeURIComponent(land)}`, body);
      toast.success(`Regels voor ${land} opgeslagen`);
      loadRegels();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    }
    setSavingLand(null);
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="instellingen-verzending-page">
      <div>
        <div className="text-sm text-muted-foreground">Instellingen</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Verzenden</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Alles rond verzenden op één plek: MyParcel-API-status, de verzendpartij per land, en
          de commerciële verzendregels die de webshop consumeert (minimum bestelbedrag, verzendkosten).
        </p>
      </div>

      {/* 1. MyParcel API-status (voorheen op Bedrijfsgegevens) */}
      <VerzendingHealthBanner showWhenOk />

      {/* 2. MyParcel — verzendpartij per land */}
      {!config ? (
        <LaadBalk label="MyParcel-instellingen laden" />
      ) : (
        <div className="bg-card border border-border rounded-xl p-6 space-y-5" data-testid="verzending-tabel">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-primary" />
            <div className="flex-1">
              <h2 className="font-medium text-lg">Verzendpartij per land <span className="text-xs text-muted-foreground font-normal">(MyParcel)</span></h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Kies per land welke koerier MyParcel gebruikt voor pakbonnen en samples. Landen zonder eigen keuze vallen terug op PostNL.
              </p>
            </div>
          </div>
          <div className="divide-y divide-border">
            {landen.map((l) => (
              <div key={l.cc} className="flex items-center justify-between py-3" data-testid={`verzend-rij-${l.cc}`}>
                <div>
                  <span className="font-medium">{l.naam}</span>
                  <span className="text-xs text-muted-foreground ml-2">{l.cc}</span>
                </div>
                <Select value={String(config[l.cc])} onValueChange={(v) => setCarrier(l.cc, v)}>
                  <SelectTrigger className="w-44" data-testid={`verzend-select-${l.cc}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {carriers.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)} data-testid={`verzend-optie-${l.cc}-${c.id}`}>
                        {c.naam}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button onClick={save} disabled={busy} data-testid="verzending-opslaan">
              <Truck className="h-4 w-4 mr-1" /> {busy ? "Bezig…" : "Opslaan"}
            </Button>
          </div>
        </div>
      )}

      {/* 3. Webshop — commerciële verzendregels per land */}
      <div className="space-y-3" data-testid="webshop-verzendregels-blok">
        <div className="flex items-center gap-2">
          <ShoppingBag className="h-5 w-5 text-primary" />
          <div>
            <h2 className="font-medium text-lg">Webshop-verzendregels</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Minimum bestelbedrag en verzendkosten die de externe webshop consumeert via <code className="font-mono text-[11px] bg-secondary px-1 rounded">GET /shop/verzendregels</code>. Individuele klanten kunnen hier van afwijken via het veld <em>minimum bestelbedrag</em> op hun organisatie.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2 p-3 rounded-lg bg-sky-50 border border-sky-200 text-sky-900 text-xs">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            Alle bedragen zijn <strong>excl. BTW</strong>. Laat een veld leeg om het uit de webshop-checkout te verbergen (bijv. "gratis verzending vanaf" kan leeg blijven als je niet met een gratis-drempel werkt).
          </div>
        </div>

        {regelsLoading ? (
          <div className="p-12 text-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin inline" />
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {regels.map((r) => {
              const w = edit[r.land] || {};
              return (
                <div key={r.land} className="bg-card border border-border rounded-xl p-5 space-y-4" data-testid={`verzendregel-${r.land}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{VLAG[r.land] || "🌍"}</span>
                      <h3 className="font-display text-lg font-medium">{r.land}</h3>
                      {NAAM_NAAR_CC[r.land] && (
                        <span className="text-[10px] uppercase text-muted-foreground">({NAAM_NAAR_CC[r.land]})</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">Valuta: <strong>{r.valuta}</strong></div>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Minimum bestelbedrag (€)</Label>
                      <Input type="number" step="0.01" placeholder="0.00"
                        value={w.minimum_bestelbedrag ?? ""}
                        onChange={(e) => zet(r.land, "minimum_bestelbedrag", e.target.value)}
                        data-testid={`min-${r.land}`} />
                      <div className="text-[11px] text-muted-foreground mt-1">Onder dit bedrag kan de klant niet afrekenen.</div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Verzendkosten (€)</Label>
                      <Input type="number" step="0.01" placeholder="Leeg = niet weergeven"
                        value={w.verzendkosten ?? ""}
                        onChange={(e) => zet(r.land, "verzendkosten", e.target.value)}
                        data-testid={`kosten-${r.land}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Gratis verzending vanaf (€)</Label>
                      <Input type="number" step="0.01" placeholder="Leeg = geen drempel"
                        value={w.gratis_verzending_vanaf ?? ""}
                        onChange={(e) => zet(r.land, "gratis_verzending_vanaf", e.target.value)}
                        data-testid={`gratis-${r.land}`} />
                    </div>
                  </div>
                  <div className="flex justify-end pt-2 border-t border-border">
                    <Button onClick={() => bewaarRegel(r.land)} disabled={savingLand === r.land} data-testid={`bewaar-${r.land}`}>
                      {savingLand === r.land ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                      Opslaan
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
